import React, { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  IconButton,
  Stack,
  type SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  type TableRowProps,
  type Theme,
  Typography,
  useTheme
} from '@mui/material';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type SortingState,
  type Updater,
  useReactTable
} from '@tanstack/react-table';
import _debounce from 'lodash/debounce';
import { mergeSx } from 'merge-sx';
import { useIntersectionObserver } from 'usehooks-ts';

import WarningRed from '@/assets/icons/warning-red.svg';
import EmptyState from '@/components/EmptyState';
import { SortDirection } from '@/hooks/useEntityList';
import useEventListener from '@/hooks/useEventListener';
import { usePreservedData } from '@/hooks/usePreservedData';
import { blur, borders, transitions } from '@/theme/constants';
import shadows from '@/theme/shadows';
import typography from '@/theme/typography';

import SortAsc from './assets/sort-asc.svg?react';
import SortDesc from './assets/sort-desc.svg?react';
import SortNone from './assets/sort-none.svg?react';

interface DataGridProps<T extends object> {
  columns: ColumnDef<T>[];
  data: T[];
  sorting?: {
    sortBy?: string | null;
    sortDirection?: SortDirection;
  };
  onSort?: (sorting: { sortBy?: string; sortDirection?: SortDirection }) => void;
  isLoading?: boolean;
  isLoadingNextPage: boolean;
  getRowProps?: (rowData: T) => TableRowProps;
  resizeWatchSelector?: string;
  emptyStateComponent?: ReactNode;
  errorStateComponent?: ReactNode;
  hasError?: boolean;
  hasNextPage?: boolean;
  onLoadNextPage?: () => void;
  getRowId?: (row: T) => string;
  hasStickyRow?: boolean;
  sx?: SxProps<Theme>;
  className?: string;
}

const getSortingWithDefault = (sortDesc?: boolean, defaultSortDirection?: SortDirection) => {
  if (typeof sortDesc === 'boolean') return sortDesc ? SortDirection.DESC : SortDirection.ASC;

  return defaultSortDirection || SortDirection.ASC;
};

const convertSortingToDataGridFormat = (
  sorting: { sortBy?: string | null; sortDirection?: SortDirection } | undefined
) => {
  if (!sorting || !sorting.sortBy || !sorting.sortDirection) return [];

  return [{ id: sorting?.sortBy, desc: sorting?.sortDirection === SortDirection.DESC }];
};

const DataGrid = <T extends object>({
  columns,
  data: immediateData,
  onSort,
  sorting,
  isLoading,
  isLoadingNextPage,
  getRowProps,
  resizeWatchSelector,
  emptyStateComponent,
  errorStateComponent,
  hasError,
  hasNextPage,
  onLoadNextPage,
  getRowId,
  hasStickyRow = true,
  sx,
  className
}: DataGridProps<T>) => {
  const tableContainerRef = useRef<HTMLTableSectionElement | null>(null);
  const tableHeadRef = useRef<HTMLTableSectionElement | null>(null);
  const data = usePreservedData(isLoading || false, immediateData);
  const isDataEmpty = !data.length;

  const { spacing } = useTheme();
  const { t } = useTranslation('common');

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (stateUpdater: Updater<SortingState>) => {
      const newSortingState = (stateUpdater as (state: SortingState) => SortingState)(
        convertSortingToDataGridFormat(sorting)
      );
      const [sortState] = (newSortingState || []) as SortingState;
      const sortBy = sortState?.id;
      const sortDirection = getSortingWithDefault(sortState?.desc);

      if (sortBy === sorting?.sortBy && sortDirection === sorting.sortDirection) return;

      onSort?.({ sortBy, sortDirection });
    },
    [onSort, sorting]
  );

  const table = useReactTable<T>({
    data,
    columns,
    state: {
      sorting: convertSortingToDataGridFormat(sorting)
    },
    onSortingChange,
    manualPagination: true,
    pageCount: -1,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...(getRowId && { getRowId })
  });

  const updateStickyPosition = useCallback(() => {
    if (!hasStickyRow) return;
    const parentTop = tableContainerRef.current?.offsetTop;
    if (!tableHeadRef.current || !parentTop) return;
    tableHeadRef.current.style.top = `${parentTop}px`;
  }, [hasStickyRow]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedStickyUpdate = useCallback(_debounce(updateStickyPosition, transitions.defaultDuration), []);

  const directAndDebouncedUpdate = useCallback(() => {
    updateStickyPosition();
    debouncedStickyUpdate();
  }, [updateStickyPosition, debouncedStickyUpdate]);

  useEffect(() => {
    directAndDebouncedUpdate();
  });

  // eslint-disable-next-line i18next/no-literal-string
  useEventListener(['resize'], debouncedStickyUpdate);

  useEffect(() => {
    if (!resizeWatchSelector) return;

    const targetElement = document.querySelector(resizeWatchSelector);
    if (!targetElement) return;

    const resizeObserver = new ResizeObserver(directAndDebouncedUpdate);
    resizeObserver.observe(targetElement);

    return () => {
      resizeObserver.unobserve(targetElement);
      resizeObserver.disconnect();
    };
  }, [directAndDebouncedUpdate, resizeWatchSelector]);

  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0
  });

  useEffect(() => {
    if (!isIntersecting || isLoading || hasError || isLoadingNextPage) return;

    onLoadNextPage?.();
  }, [isLoadingNextPage, isLoading, hasError, isIntersecting]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box margin={spacing(2, 0)} sx={sx} className={className}>
      <TableContainer
        ref={tableContainerRef}
        component={Box}
        sx={{
          borderRadius: spacing(0.5),
          bgcolor: 'background.default',
          boxShadow: shadows[3],
          border: borders.default,
          borderColor: 'grey.400',
          borderTop: 'none',
          overflowX: 'unset'
        }}
      >
        <Table
          sx={{
            borderCollapse: 'collapse',
            zIndex: 2,
            ...(!hasStickyRow && {
              position: 'relative'
            })
          }}
          role="table"
        >
          <TableHead
            ref={tableHeadRef}
            sx={{
              zIndex: 2,
              bgcolor: 'background.default',
              boxShadow: shadows[3],
              ...(hasStickyRow && {
                position: 'sticky',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: 0,
                  borderTop: borders.default,
                  borderColor: 'grey.400'
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  height: 0,
                  borderTop: borders.default,
                  borderColor: 'grey.400'
                }
              })
            }}
          >
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow
                key={headerGroup.id}
                role="row"
                sx={{
                  '& .MuiTableCell-root:first-of-type': {
                    borderLeft: 'none',
                    borderRight: 'none'
                  },
                  '& .MuiTableCell-root:last-of-type': {
                    borderLeft: 'none',
                    borderRight: 'none'
                  }
                }}
              >
                {headerGroup.headers.map(header => (
                  <TableCell
                    key={header.id}
                    sx={{
                      borderLeft: borders.default,
                      borderRight: borders.default,
                      borderColor: 'grey.300',
                      p: spacing(2.5, 2),
                      cursor: header.column.getCanSort() ? 'pointer' : 'default'
                    }}
                    scope="col"
                    colSpan={1}
                    role="columnheader"
                    onClick={header.column.getToggleSortingHandler()}
                    width={header.column.columnDef.size}
                  >
                    <Stack direction="row" alignItems="center" gap={1.25}>
                      <Typography
                        component={Box}
                        sx={{
                          ...typography.th,
                          fontWeight: typography.fontWeightMedium,
                          textTransform: 'uppercase',
                          color: 'grey.700',
                          whiteSpace: 'nowrap',
                          lineHeight: spacing(2.5)
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </Typography>
                      {header.column.getCanSort() && (
                        <IconButton
                          sx={{
                            p: 0,
                            '& .SortIcon-root': { width: spacing(2.25), height: spacing(2.25) }
                          }}
                        >
                          {header.column.getIsSorted() === SortDirection.DESC && <SortDesc className="SortIcon-root" />}
                          {header.column.getIsSorted() === SortDirection.ASC && <SortAsc className="SortIcon-root" />}
                          {!header.column.getIsSorted() && <SortNone className="SortIcon-root" />}
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          <TableBody
            role="table"
            sx={{
              position: 'relative',
              '& .MuiTableRow-root:hover': {
                bgcolor: 'primary.50'
              }
            }}
          >
            {isLoading && (
              <TableRow
                sx={{
                  backdropFilter: blur.default,
                  position: isDataEmpty ? 'static' : 'absolute',
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                  bgcolor: 'transparent',
                  '.MuiTableBody-root &:hover': {
                    bgcolor: 'transparent'
                  }
                }}
              >
                <TableCell
                  sx={{
                    border: 'none',
                    justifyContent: 'center',
                    height: '100%',
                    display: isDataEmpty ? 'table-cell' : 'flex'
                  }}
                  colSpan={columns.length}
                  rowSpan={columns.length}
                >
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      minHeight: isDataEmpty ? '300px' : 'unset'
                    }}
                  >
                    <CircularProgress />
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (isDataEmpty || hasError) && (
              <TableRow
                sx={{
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                  bgcolor: 'transparent',
                  '.MuiTableBody-root &:hover': {
                    bgcolor: 'transparent'
                  }
                }}
              >
                <TableCell
                  sx={{
                    border: 'none',
                    justifyContent: 'center',
                    height: '100%'
                  }}
                  colSpan={columns.length}
                  rowSpan={columns.length}
                >
                  <Stack
                    justifyContent="center"
                    alignItems="center"
                    sx={{
                      minHeight: '300px'
                    }}
                  >
                    {!hasError &&
                      (emptyStateComponent || (
                        <EmptyState
                          imageUrl="/images/empty-state-generic.svg"
                          imageAlt={t('empty.noDataAlt')}
                          imageSx={{ width: 200 }}
                        >
                          <Typography
                            className="EmptyState-label"
                            variant="body2"
                            sx={{ textAlign: 'center', fontWeight: typography.fontWeightMedium, color: 'grey.700' }}
                          >
                            {t('empty.noData')}
                          </Typography>
                        </EmptyState>
                      ))}
                    {hasError &&
                      (errorStateComponent || (
                        <EmptyState imageUrl={WarningRed} imageAlt={t('error.generalMessage')} imageSx={{ width: 200 }}>
                          <Typography
                            className="EmptyState-label"
                            variant="body2"
                            sx={{ textAlign: 'center', fontWeight: typography.fontWeightMedium, color: 'grey.700' }}
                          >
                            {t('error.generalMessage')}
                          </Typography>
                        </EmptyState>
                      ))}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
            {!hasError &&
              table.getRowModel().rows.map(row => {
                const rowProps = getRowProps?.(row.original);

                return (
                  <TableRow
                    {...rowProps}
                    key={row.id}
                    sx={mergeSx<Theme>(
                      {
                        '& .MuiTableCell-root:first-of-type': {
                          borderLeft: 'none',
                          borderRight: 'none'
                        },
                        '& .MuiTableCell-root:last-of-type': {
                          borderLeft: 'none',
                          borderRight: 'none'
                        }
                      },
                      rowProps?.sx
                    )}
                    role="row"
                  >
                    {row.getAllCells().map(cell => (
                      <TableCell
                        key={cell.id}
                        sx={{
                          borderLeft: borders.default,
                          borderRight: borders.default,
                          borderColor: 'grey.300',
                          fontSize: typography.body2?.fontSize,
                          p: 0,
                          '& .MuiTypography-root': {
                            fontSize: typography.body2?.fontSize
                          }
                        }}
                        role="cell"
                        width={cell.column.columnDef.size}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            {!isLoading && hasNextPage && (
              <TableRow ref={ref}>
                <TableCell
                  sx={{
                    border: 'none'
                  }}
                  colSpan={columns.length}
                >
                  <Stack
                    sx={{
                      justifyContent: 'center',
                      flexDirection: 'row',
                      minHeight: 1
                    }}
                  >
                    {isLoadingNextPage && <CircularProgress />}
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataGrid;
